/**
 * DashboardLayout Component
 * Main layout wrapper for all dashboard pages
 * 
 * Refactored for maintainability:
 * - Header, Sidebar, Footer extracted to separate components
 * - Menu configuration moved to config/menuConfig.js
 * - ValueAddedServices extracted to layout/ValueAddedServices.js
 * - Mobile-responsive with collapsible sidebar
 */
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { FaAngleDoubleRight } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { getMenuItemsForRole } from '../config/menuConfig';
import { DashboardHeader, DashboardSidebar, DashboardFooter } from './layout';
import ChangePasswordModal from './ChangePasswordModal';
import FloatingWhatsApp from './FloatingWhatsApp';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // UI state — desktop sidebar state persists across page navigations & reloads.
  // On mobile (<lg) sidebar always starts closed; on desktop honour user's last choice.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined' || window.innerWidth < 1024) return false;
    const saved = localStorage.getItem('sidebarOpen');
    return saved === null ? true : saved === 'true';
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showForcedPasswordChange, setShowForcedPasswordChange] = useState(false);

  // Check if user needs to change password on mount
  useEffect(() => {
    if (user?.must_change_password) {
      setShowForcedPasswordChange(true);
    }
  }, [user]);

  // Handle window resize — open sidebar on mobile→desktop transition only if
  // the user hasn't explicitly minimized it. Otherwise leave it collapsed.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        const saved = localStorage.getItem('sidebarOpen');
        if (saved === null) setSidebarOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get filtered menu items based on user role
  const menuItems = getMenuItemsForRole(user?.role);

  // Handlers
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleToggleSidebar = () => {
    setSidebarOpen((prev) => {
      const next = !prev;
      // Only persist desktop preference. Mobile open/close is transient.
      if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
        localStorage.setItem('sidebarOpen', String(next));
      }
      return next;
    });
  };

  const handleCloseSidebar = () => {
    // Only close on mobile
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const handleOpenChangePassword = () => {
    setShowChangePassword(true);
  };

  const handleCloseChangePassword = () => {
    setShowChangePassword(false);
  };

  const handlePasswordChangeSuccess = () => {
    setShowForcedPasswordChange(false);
    setShowChangePassword(false);
    // Refresh to update user data
    window.location.reload();
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0A1929 0%, #1E3A5F 100%)' }}>
      {/* Header */}
      <DashboardHeader
        user={user}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={handleToggleSidebar}
        onChangePassword={handleOpenChangePassword}
        onLogout={handleLogout}
      />

      {/* Main Content Area */}
      <div className="flex flex-col min-h-[calc(100vh-64px)] sm:min-h-[calc(100vh-76px)]">
        <div className="flex flex-1 relative">
          {/* Sidebar */}
          <DashboardSidebar 
            menuItems={menuItems} 
            userRole={user?.role}
            userPlan={user?.plan}
            isOpen={sidebarOpen}
            onClose={handleCloseSidebar}
          />

          {/* Floating "Open Sidebar" pill — visible on desktop only when collapsed.
              Lets users restore the sidebar without hunting for the small hamburger. */}
          {!sidebarOpen && (
            <button
              onClick={handleToggleSidebar}
              title="Show sidebar"
              data-testid="show-sidebar-btn"
              className="hidden lg:flex items-center justify-center fixed left-0 top-1/2 -translate-y-1/2 z-30
                         w-7 h-16 rounded-r-lg
                         bg-gradient-to-b from-[#D4AF37] to-[#FFD700] text-[#0A1929]
                         shadow-lg shadow-black/40 hover:w-9 hover:from-[#FFD700] hover:to-[#FFE34D]
                         transition-all duration-200"
            >
              <FaAngleDoubleRight size={14} />
            </button>
          )}

          {/* Main Content */}
          <main className="flex-1 p-3 sm:p-6 overflow-auto w-full">
            <Outlet />
          </main>
        </div>

        {/* Footer */}
        <DashboardFooter />
      </div>

      {/* Change Password Modal - Forced on first login */}
      <ChangePasswordModal
        isOpen={showForcedPasswordChange}
        onClose={() => {}} // Cannot close forced modal
        isForced={true}
        onSuccess={handlePasswordChangeSuccess}
      />

      {/* Change Password Modal - Voluntary */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={handleCloseChangePassword}
        isForced={false}
        onSuccess={handleCloseChangePassword}
      />

      {/* Floating WhatsApp Button - Only for advisors, managers, and super admins (not clients) */}
      {user?.role && user.role !== 'client' && (
        <FloatingWhatsApp 
          phoneLink="https://wa.link/c15uzz"
          message="Need help? Chat with us!"
        />
      )}
    </div>
  );
};

export default DashboardLayout;
